// ─────────────────────────────────────────────────────────────────────────────
// TourRunner — orchestrates the guided tour.
//
// Presentation model (mobile-first, ZERO external tour library):
//   • We draw our OWN spotlight ring (a pointer-events:none div around the
//     target). There is no page-dimming overlay, so the highlighted element and
//     the entire app stay fully interactive.
//   • The instruction text lives in our own <TourCoachmark>, which starts
//     EXPANDED (full card near the target) and auto-collapses to a small draggable
//     CHIP after 5s OR on tap-outside. Tapping the chip re-expands it; dragging it
//     to the bottom drop-zone closes the tour. The ring stays on the target the
//     whole time, so content is never blocked.
//
// Responsibilities (the hard parts a naive tour gets wrong):
//   1. Targeting   — polls for the step's target element to mount before
//                    highlighting, so async pages don't leave the spotlight stuck.
//   2. Cross-route — on tour start always force-navigates to step 0's route so
//                    the tour is never confused by the user's current location.
//                    Between steps: auto-navigates when needed, advances on arrival.
//   3. Advancement — per-step trigger: 'click' / 'route' / 'next'.
//   4. Live rect   — tracks the target's bounding rect (scroll/resize) so the
//                    coachmark + ring stay glued to it.
//
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './tour.css';
import { useTour } from './TourContext';
import TourCoachmark from './TourCoachmark';

const POLL_INTERVAL = 120;   // ms between element-mount checks
const POLL_TIMEOUT = 6000;   // give up waiting for a target after this

// Normalise a path: strip trailing slash + query/hash for comparison.
const normPath = (p) => (p || '/').split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';

const readRect = (el) => {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right };
};

export default function TourRunner() {
  const { activeFlow, stepIndex, isRunning, nextStep, stopTour, startSignal } = useTour();
  const location = useLocation();
  const navigate = useNavigate();

  // The live rect of the currently highlighted element. Drives the ring + card.
  const [rect, setRect] = useState(null);
  // True once a step has mounted its spotlight ring.
  const [ownHighlight, setOwnHighlight] = useState(false);

  const pollRef = useRef(null);
  const cleanupClickRef = useRef(null);
  const rafRef = useRef(null);

  // Stable refs so effects can read latest controls without re-running.
  const nextStepRef = useRef(nextStep);
  const stopTourRef = useRef(stopTour);
  nextStepRef.current = nextStep;
  stopTourRef.current = stopTour;

  // Clear only the event listeners and polls (NOT rect/ring), called between steps.
  // This prevents the spotlight ring from flashing off between steps.
  const cleanupListeners = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (cleanupClickRef.current) { cleanupClickRef.current(); cleanupClickRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);

  // Full teardown: listeners + ring. Called when tour stops entirely.
  const teardown = useCallback(() => {
    cleanupListeners();
    setRect(null);
    setOwnHighlight(false);
  }, [cleanupListeners]);

  // On tour start (startSignal increments), always force-navigate to step 0's
  // route. This resets location state cleanly so route-advance guards start
  // fresh regardless of which page the user was on when they launched the tour.
  // Without this, starting the tour while already on /trade would cause step 0
  // (route: '/') to be skipped because the route-advance effect would see
  // /trade matching step 1's route immediately.
  const lastHandledSignalRef = useRef(0);
  useEffect(() => {
    if (!isRunning || !activeFlow || startSignal === 0) return;
    if (startSignal === lastHandledSignalRef.current) return;
    lastHandledSignalRef.current = startSignal;

    const step0 = activeFlow.steps[0];
    if (step0) {
      navigate(step0.route, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startSignal, isRunning, activeFlow]);

  // Main effect: react to (flow, step, route) changes.
  useEffect(() => {
    if (!isRunning || !activeFlow) { teardown(); return; }

    const step = activeFlow.steps[stepIndex];
    if (!step) { stopTourRef.current(); return; }

    const onCorrectRoute = normPath(location.pathname) === normPath(step.route);

    // Not on the step's route → auto-navigate there.
    // Exception: if this is a `route`-advance step and we're already on the
    // NEXT step's route, the route-advance effect will bump stepIndex — don't fight it.
    if (!onCorrectRoute) {
      cleanupListeners(); // clear listeners only; keep ring so there's no flash
      const nextDef = activeFlow.steps[stepIndex + 1];
      const onNextStepsRoute =
        (step.advanceOn || 'next') === 'route' &&
        nextDef &&
        normPath(location.pathname) === normPath(nextDef.route);
      if (!onNextStepsRoute) navigate(step.route);
      return;
    }

    // On the right route — clear previous step's listeners before polling.
    cleanupListeners();

    // Poll for the target, then spotlight it.
    let elapsed = 0;
    const show = () => {
      const el = document.querySelector(step.target);
      if (!el) {
        elapsed += POLL_INTERVAL;
        if (elapsed >= POLL_TIMEOUT) {
          clearInterval(pollRef.current); pollRef.current = null;
          nextStepRef.current(); // target never appeared — skip rather than trap
        }
        return;
      }
      clearInterval(pollRef.current); pollRef.current = null;
      mountSpotlight(step, el);
    };

    pollRef.current = setInterval(show, POLL_INTERVAL);
    show();

    // Only clean up listeners on unmount/re-run, NOT the rect/ring (prevents flash).
    return cleanupListeners;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, activeFlow, stepIndex, location.pathname]);

  // Spotlight a single element (own ring), wire advancement, and start tracking
  // its rect for the coachmark.
  const mountSpotlight = (step, el) => {
    setOwnHighlight(true);

    let lastRect = readRect(el);
    setRect(lastRect);

    // The element the rect currently tracks. Updated if the spotlight shifts to
    // an advanceTarget (e.g. from the search box to a result's BUY button).
    let trackedEl = el;

    // Keep the rect in sync while the user scrolls / the layout shifts — but
    // CHEAPLY. Only setState when the rect moved by more than a pixel or two
    // (avoids a full re-render every scroll frame).
    const moved = (a, b) =>
      Math.abs(a.top - b.top) > 1 || Math.abs(a.left - b.left) > 1 ||
      Math.abs(a.width - b.width) > 1 || Math.abs(a.height - b.height) > 1;
    const sync = () => {
      if (!trackedEl || !document.body.contains(trackedEl)) return;
      const r = readRect(trackedEl);
      if (moved(r, lastRect)) { lastRect = r; setRect(r); }
    };
    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(sync);
    };
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // 'click' advancement. Two modes:
    //   • default        → click the highlighted element itself.
    //   • advanceTarget  → highlight `el`, but advance when a (possibly
    //                      later-rendered) element matching `advanceTarget` is
    //                      clicked. Uses delegation on document so it catches
    //                      results that mount after the step starts.
    let directHandler = null;
    let delegatedHandler = null;
    let shiftPoll = null;
    // Guard so a single interaction can only advance once per step.
    let advanced = false;
    const advanceOnce = () => {
      if (advanced) return;
      advanced = true;
      setTimeout(() => nextStepRef.current(), 0);
    };

    if ((step.advanceOn || 'next') === 'click') {
      if (step.advanceTarget) {
        delegatedHandler = (e) => {
          // Walk up from the clicked element and check each ancestor against
          // every element that matches advanceTarget — this works reliably for
          // both simple selectors ("button") and compound ones
          // ("[data-tour="x"] button") without relying on closest() with
          // descendant combinators, which some browsers handle inconsistently.
          const targets = Array.from(document.querySelectorAll(step.advanceTarget));
          const hit = targets.some(t => t === e.target || t.contains(e.target));
          if (hit) advanceOnce();
        };
        document.addEventListener('click', delegatedHandler, { capture: true });

        // Shift the spotlight ring onto the first advanceTarget element once
        // it appears in the DOM (handles async-mounted content like the modal).
        shiftPoll = setInterval(() => {
          const t = document.querySelector(step.advanceTarget);
          if (t && t !== trackedEl) {
            trackedEl = t;
            lastRect = readRect(t);
            setRect(lastRect);
          }
        }, 250);
      } else {
        directHandler = () => advanceOnce();
        el.addEventListener('click', directHandler, { capture: true });
      }
    }

    cleanupClickRef.current = () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
      if (shiftPoll) clearInterval(shiftPoll);
      if (directHandler) el.removeEventListener('click', directHandler, { capture: true });
      if (delegatedHandler) document.removeEventListener('click', delegatedHandler, { capture: true });
    };
  };

  // Route-advance effect: advance when the user navigates to the NEXT step's
  // route (lets a highlighted nav item move the tour forward by navigation).
  // The guard is keyed by "flowId:stepIndex" (not just stepIndex) so it resets
  // between tour runs — otherwise a second tour that reuses step 0 would see the
  // stale guard from the first run and never advance.
  const advancedKeyRef = useRef(null);
  useEffect(() => {
    if (!isRunning || !activeFlow) return;
    const step = activeFlow.steps[stepIndex];
    if (!step || (step.advanceOn || 'next') !== 'route') return;
    const nextStepDef = activeFlow.steps[stepIndex + 1];
    if (!nextStepDef) return;

    if (normPath(location.pathname) === normPath(nextStepDef.route)) {
      const key = `${activeFlow.id}:${stepIndex}`;
      if (advancedKeyRef.current === key) return;
      advancedKeyRef.current = key;
      nextStepRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isRunning, activeFlow, stepIndex]);

  // Reset the route-advance guard whenever a tour stops, so the NEXT tour starts
  // clean even if it reuses the same flow/step indices.
  useEffect(() => {
    if (!isRunning) {
      advancedKeyRef.current = null;
      teardown();
    }
  }, [isRunning, teardown]);

  // Clean up on unmount.
  useEffect(() => teardown, [teardown]);

  if (!isRunning || !activeFlow || !rect) return null;

  const step = activeFlow.steps[stepIndex];
  if (!step) return null;
  const advanceOn = step.advanceOn || 'next';
  const isLast = stepIndex >= activeFlow.steps.length - 1;

  return (
    <>
      {/* Our spotlight ring — the ONLY highlight (no driver.js). pointer-events:
          none so it never blocks the target or anything else on the page. */}
      {ownHighlight && (
        <div
          aria-hidden
          className="tour-ring"
          style={{
            position: 'fixed', zIndex: 99998, pointerEvents: 'none',
            top: rect.top - 6, left: rect.left - 6,
            width: rect.width + 12, height: rect.height + 12,
            borderRadius: 12,
            border: '3px solid rgba(99,102,241,1)',
            boxShadow: '0 0 0 3px rgba(99,102,241,0.35), 0 0 16px 3px rgba(99,102,241,0.6)',
            willChange: 'top, left',
          }}
        />
      )}
      <TourCoachmark
        key={`${activeFlow.id}-${stepIndex}`}   // remount per step → resets to expanded
        rect={rect}
        title={step.title}
        text={step.text}
        stepNumber={stepIndex + 1}
        totalSteps={activeFlow.steps.length}
        showNext={advanceOn === 'next'}
        isLast={isLast}
        onNext={() => { if (isLast) { teardown(); stopTour(); } else nextStep(); }}
        onSkip={() => { teardown(); stopTour(); }}
      />
    </>
  );
}
